import { useRef, useState } from 'react';
import { assignLabel, removeLabel } from '@/services/workflow/labels';
import { useNotification } from '@/components/notifications';
import * as Sentry from '@sentry/react';

interface UseToggleWorkflowLabelOptions {
  orgId: string | undefined;
  workflowId: string;
  onSuccess?: () => void;
  /** Fires after a failed toggle reverts the optimistic selection, with the
   *  corrected assigned set so callers can roll back derived state (e.g. the
   *  workflow store) to match. */
  onError?: (assignedIds: Set<string>) => void;
}

export function useToggleWorkflowLabel({
  orgId,
  workflowId,
  onSuccess,
  onError,
}: UseToggleWorkflowLabelOptions) {
  const [assignedIds, setAssignedIds] = useState<Set<string>>(new Set());
  // Mirror of assignedIds updated synchronously so concurrent toggles and the
  // error-path revert always compute from the latest set, not a stale closure.
  const assignedRef = useRef<Set<string>>(new Set());
  // Track in-flight requests per label so several labels can be toggled in one
  // sitting without blocking each other — the picker stays interactive.
  const [pendingIds, setPendingIds] = useState<Set<string>>(new Set());
  // Mirror of pendingIds updated synchronously so a rapid second click reads the
  // in-flight set before React re-renders, instead of passing the stale-state
  // guard and firing the opposite request mid-flight.
  const pendingRef = useRef<Set<string>>(new Set());
  const notification = useNotification();

  const applyAssigned = (next: Set<string>) => {
    assignedRef.current = next;
    setAssignedIds(next);
  };

  const applyPending = (next: Set<string>) => {
    pendingRef.current = next;
    setPendingIds(next);
  };

  const initFromLabels = (labelIds: string[]) => {
    applyAssigned(new Set(labelIds));
  };

  const toggle = async (labelId: string) => {
    if (!orgId || pendingRef.current.has(labelId)) return;

    const wasAssigned = assignedRef.current.has(labelId);

    const optimistic = new Set(assignedRef.current);
    if (wasAssigned) {
      optimistic.delete(labelId);
    } else {
      optimistic.add(labelId);
    }
    applyAssigned(optimistic);

    applyPending(new Set(pendingRef.current).add(labelId));
    try {
      if (wasAssigned) {
        await removeLabel(orgId, workflowId, labelId);
      } else {
        await assignLabel(orgId, workflowId, labelId);
      }
      onSuccess?.();
    } catch (error) {
      const reverted = new Set(assignedRef.current);
      if (wasAssigned) {
        reverted.add(labelId);
      } else {
        reverted.delete(labelId);
      }
      applyAssigned(reverted);
      onError?.(reverted);
      notification.error('Failed to update label');
      Sentry.captureException(error, {
        extra: { context: 'toggle-workflow-label', labelId, workflowId },
      });
    } finally {
      const next = new Set(pendingRef.current);
      next.delete(labelId);
      applyPending(next);
    }
  };

  return {
    assignedIds,
    isToggling: pendingIds.size > 0,
    pendingIds,
    toggle,
    initFromLabels,
  };
}
